/*
 * Copyright (c) 2023 - 2026 Bluemate B.V.
 */
import './style/contextAccount.scss';

import React, {useContext, useMemo} from 'react';
import {useTranslation} from 'react-i18next';

import {UserRole} from '../../../util/interfaces';

import {PortalSelectItem, PortalSelectOption} from '../../../util/types';

import {AppContext} from '../../misc/appContext';

import Account from '../../../models/accountModel';

import PortalSelectController from '../../controls/portalSelect/portalSelectController';

interface Props {
    onChangeAccount: (id: string) => void;
    // null hides the in-component label (e.g. when a section title already labels it,
    // as in the bottom-nav "Meer" menu). Undefined falls back to the default label.
    label?: string | null;
    // 'top' opens the dropdown upward — needed in the bottom-nav menu where the
    // select sits just above the fixed bar. Defaults to 'bottom' (header).
    itemsPosition?: 'top' | 'bottom';
}

export default function ContextAccount(props: Props) {
    const {t} = useTranslation();
    const context = useContext(AppContext);
    const {accounts, account} = context.accountContext;
    const {onChangeAccount} = props;
    const [ownedAccounts, accessAccounts]: [Array<Account>, Array<Account>] = useMemo(() => {
        const owned: Array<Account> = [];
        const access: Array<Account> = [];
        for (const account of accounts) {
            if (account.userRole === UserRole.OWNER) owned.push(account);
            else access.push(account);
        }
        return [owned, access];
    }, [accounts]);
    const items: Array<PortalSelectItem> = useMemo(() => {
        const ownedOptions: Array<PortalSelectOption> = ownedAccounts
            .map((account: Account): PortalSelectOption => ({label: account.labelWithSellerID, value: account.id}))
            .concat({value: '', label: t('account.addSalesAccount', '+ Verkoopaccount'), action: true});
        if (!accessAccounts.length) return ownedOptions;
        return [
            {groupLabel: t('account.ownAccounts', 'Eigen accounts'), items: ownedOptions},
            {
                groupLabel: t('account.otherAccounts', 'Andere accounts'),
                items: accessAccounts.map((account: Account) => ({
                    label: account.labelWithSellerID,
                    value: account.id
                }))
            }
        ];
    }, [ownedAccounts, accessAccounts, t]);

    const label = props.label === undefined ? t('account.salesAccount', 'Verkoopaccount:') : (props.label ?? undefined);

    return (
        <PortalSelectController
            className='context-account'
            items={items}
            value={account?.id ?? ''}
            onChange={(value: string | null) => onChangeAccount(value ?? '')}
            label={label}
            itemsPosition={props.itemsPosition}
            itemsHeight='70dvh'
        />
    );
}
